import { z } from "zod";
import { Id, IsoDate, Locale, Problem } from "./common.ts";
import { CellValue, VizType, WidgetQuery, type WidgetInput } from "./dashboards.ts";
import { AgentSurface } from "./agents.ts";
import { AiLabels, CommenterMark, FlagKey, FlaggableEntity, RuleInput } from "./domain.ts";
import type { RouteDef } from "./routes.ts";

/**
 * Malfak Copilot (plan 14 §1–5): a generative-UI agent on Dify.
 *
 * - The agent answers with a `CopilotResponse`: typed blocks the web app renders as live, interactive UI
 *   (never raw paragraphs only), actions (navigate/open/filter/run) and proposals (writes the user reviews).
 * - Every data block carries `source: WidgetQuery`, so it can be pinned to a dashboard and re-queried live.
 * - The agent can READ what the user can read and can only PROPOSE writes. Proposals are stored server-side
 *   (approvals, kind `copilot`) with a signature; the user's review (include/exclude/edit) is executed through
 *   the normal API command paths with the user's own permissions and policies.
 * - Tool calls from Dify carry a short-lived scoped tool token (user, org, role, scopes, request/session id).
 */

// ---------------------------------------------------------------- vocabulary
export const CopilotEntityType = z.enum(["comment", "post", "publication", "conversation", "contact", "account", "dashboard", "job", "rule", "approval"]);
export const CopilotScope = z.enum(["read", "propose"]);
export const NumberKind = z.enum(["number", "percent", "currency", "duration"]);
export const EntityRef = z.object({ type: CopilotEntityType, id: Id });

/** What the user is looking at: the agent resolves "here", "this post", "these" with it. */
export const CopilotContext = z.object({
  /** Current route path, e.g. `/content/posts/<id>` or `/inbox`. */
  page: z.string().max(300).default("/"),
  title: z.string().max(200).optional(),
  entity: EntityRef.nullable().optional(),
  selection: z.object({ type: CopilotEntityType, ids: z.array(Id).min(1).max(200) }).nullable().optional(),
  /** Visible filters of the current page (inbox state, sentiment…), as plain strings. */
  filters: z.record(z.string().max(40), z.string().max(200)).optional(),
  locale: Locale.optional(),
  timezone: z.string().max(64).optional(),
});

// ---------------------------------------------------------------- blocks
const blockBase = {
  /** Stable within a message (pin, keys, partial → final replacement). */
  id: z.string().min(1).max(64),
  title: z.string().max(200).optional(),
};
export const TextBlock = z.object({ ...blockBase, type: z.literal("text"), markdown: z.string().max(8000) });
export const StatsBlock = z.object({
  ...blockBase,
  type: z.literal("stats"),
  items: z.array(z.object({ label: z.string().max(80), value: z.number().finite().nullable(), delta: z.number().finite().nullable().optional(), format: NumberKind.default("number") })).min(1).max(8),
  source: WidgetQuery,
});
export const ChartKind = z.enum(["line", "bar", "area", "donut", "heatmap"]);
export const ChartBlock = z.object({
  ...blockBase,
  type: z.literal("chart"),
  kind: ChartKind,
  title: z.string().max(200),
  series: z.array(z.object({ name: z.string().max(80), points: z.array(z.object({ x: z.union([z.string().max(64), z.number()]), y: z.number().finite().nullable() })).max(500) })).max(8),
  source: WidgetQuery,
});
export const TableColumn = z.object({ key: z.string().max(64), label: z.string().max(80), kind: z.enum(["text", "number", "date", "entity", "badge"]).default("text") });
export const TableBlock = z.object({
  ...blockBase,
  type: z.literal("table"),
  columns: z.array(TableColumn).min(1).max(12),
  rows: z.array(z.record(z.string(), CellValue)).max(200),
  entity: z.enum(["comment", "post", "conversation", "contact"]).optional(),
  source: WidgetQuery,
});
export const CommentsBlock = z.object({
  ...blockBase,
  type: z.literal("comments"),
  /** Entity references only: the UI loads each comment live (permission-checked, actionable). */
  ids: z.array(Id).max(100),
  total: z.number().int().nonnegative().optional(),
  highlight: z.object({ intent: AiLabels.shape.intent.unwrap().optional(), sentiment: AiLabels.shape.sentiment.unwrap().optional(), query: z.string().max(200).optional() }).optional(),
  source: WidgetQuery,
});
export const PostsBlock = z.object({ ...blockBase, type: z.literal("posts"), ids: z.array(Id).max(60), source: WidgetQuery });
export const ConversationsBlock = z.object({ ...blockBase, type: z.literal("conversations"), ids: z.array(Id).max(60), source: WidgetQuery });
export const ContactBlock = z.object({ ...blockBase, type: z.literal("contact"), contactId: Id });
export const TimelineBlock = z.object({
  ...blockBase,
  type: z.literal("timeline"),
  items: z.array(z.object({ at: IsoDate, title: z.string().max(200), body: z.string().max(500).optional(), ref: EntityRef.optional() })).max(50),
});
export const CalloutBlock = z.object({ ...blockBase, type: z.literal("callout"), tone: z.enum(["info", "warn", "success", "danger"]), title: z.string().max(200), body: z.string().max(2000).default("") });
export const JobBlock = z.object({ ...blockBase, type: z.literal("job"), jobId: Id });
export const FormField = z.object({
  name: z.string().regex(/^[a-zA-Z][\w]{0,39}$/),
  label: z.string().max(80),
  kind: z.enum(["text", "textarea", "select", "account", "post", "date", "number"]),
  options: z.array(z.object({ value: z.string().max(100), label: z.string().max(100) })).max(30).optional(),
  required: z.boolean().default(false),
  placeholder: z.string().max(120).optional(),
  default: z.string().max(500).optional(),
});
export const FormBlock = z.object({ ...blockBase, type: z.literal("form"), fields: z.array(FormField).min(1).max(8), submitLabel: z.string().max(40) });

/**
 * ALP-83: posts, each with its filtered comments nested underneath ("the last three posts with their negative comments").
 * Entity references only (the UI loads posts/comments live). `source` = the comments query (post_id in the groups' posts +
 * the comment filters), `postsSource` = the posts query: pinning creates a posts widget + a comments widget.
 */
export const PostsWithCommentsBlock = z.object({
  ...blockBase,
  type: z.literal("posts_with_comments"),
  /** Filter chip summary shown on top, in the user's language ("negative · 1–15 March"). */
  filterSummary: z.string().max(200).default(""),
  groups: z.array(z.object({
    postId: Id,
    commentIds: z.array(Id).max(50),
    /** Comments of this post matching the filters (may exceed commentIds: "show more"). */
    totalMatching: z.number().int().nonnegative(),
    filterSummary: z.string().max(200).default(""),
  })).max(10),
  source: WidgetQuery,
  postsSource: WidgetQuery.optional(),
});

export const CopilotBlock = z.discriminatedUnion("type", [
  TextBlock, StatsBlock, ChartBlock, TableBlock, CommentsBlock, PostsBlock, ConversationsBlock, ContactBlock, TimelineBlock, CalloutBlock, JobBlock, FormBlock,
  PostsWithCommentsBlock,
]);
/** Block types that carry a live `source` query (pinnable as a live widget). */
export const DATA_BLOCK_TYPES = ["stats", "chart", "table", "comments", "posts", "conversations", "posts_with_comments"] as const;

// ---------------------------------------------------------------- actions (never write)
export const CopilotReadTool = z.enum([
  "search_comments", "list_comments", "list_deleted", "get_post", "list_posts", "post_insights", "account_analytics", "best_times",
  "list_conversations", "get_contact", "list_people", "list_rules", "simulate_rule", "list_jobs", "sync_health", "search_knowledge", "describe_datasets",
  "calendar_events", // ALP-80: subscribed national/religious/international days + crises (read-only, org-scoped)
  "list_posts_with_comments", // ALP-83: posts with their filtered comments (posts_with_comments block)
]);
export const CopilotAction = z.discriminatedUnion("type", [
  /** In-app path only (no scheme/host): e.g. `/inbox`, `/content/posts/<id>`. */
  z.object({ type: z.literal("navigate"), label: z.string().max(60), to: z.string().regex(/^\/(?!\/)[\w\-./$%~]*$/, "in-app path only").max(300), search: z.record(z.string(), z.union([z.string().max(200), z.number(), z.boolean()])).optional() }),
  z.object({ type: z.literal("open"), label: z.string().max(60).optional(), entity: CopilotEntityType, id: Id }),
  z.object({ type: z.literal("filter"), label: z.string().max(60), page: z.enum(["inbox", "people", "jobs", "posts", "dashboards"]), query: z.record(z.string().max(40), z.union([z.string().max(200), z.number(), z.boolean()])) }),
  /** Re-run a read-only tool without the LLM (e.g. "show 20 more"); the result arrives as new blocks. */
  z.object({ type: z.literal("run"), label: z.string().max(60), tool: CopilotReadTool, args: z.record(z.string(), z.unknown()) }),
]);

// ---------------------------------------------------------------- proposals (propose → review → confirm)
export const ProposalKind = z.enum([
  "delete_comments", "hide_comments", "unhide_comments", "reply", "private_reply", "send_message", "flag", "archive", "pin",
  "mark_commenter", "reload_comments", "resync_account", "create_rule", "schedule_post",
  // ALP-83: draft replies with one of the org's AI agents (each draft becomes an approval), assign items to a member
  "agent_reply", "assign",
]);
/** Kinds whose effect can be undone from the result toast. */
export const REVERSIBLE_KINDS: readonly ProposalKindT[] = ["hide_comments", "unhide_comments", "flag", "archive", "pin", "mark_commenter"];
/** Kinds rendered with danger styling (irreversible on the channel). */
export const DESTRUCTIVE_KINDS: readonly ProposalKindT[] = ["delete_comments"];
/** Item text the reviewer may edit (reply text). */
export const EDITABLE_KINDS: readonly ProposalKindT[] = ["reply", "private_reply", "send_message"];
/** `undoing` (ALP-80): an undo job is reverting the applied items (a running bulk job is cancelled first). */
export const ProposalStatus = z.enum(["pending", "executing", "executed", "partially_executed", "failed", "rejected", "expired", "undoing", "undone"]);
export const ProposalItem = z.object({
  /** Stable item key inside the proposal (`i1`, `i2`, …). */
  id: z.string().max(16),
  ref: EntityRef.nullable(),
  preview: z.object({ title: z.string().max(200), body: z.string().max(2200).nullable(), meta: z.string().max(200).nullable() }),
  /** Kind-specific payload (e.g. `{ text }` for replies); server-owned, the browser can't change it except `text` on editable items. */
  payload: z.record(z.string(), z.unknown()),
  editable: z.boolean(),
  /** Policy problem found while proposing (e.g. "24h window closed"): shown, item excluded by default. */
  warning: z.string().max(200).nullable(),
  blocked: z.boolean(),
});
export const Proposal = z.object({
  id: Id,
  kind: ProposalKind,
  title: z.string().max(140),
  summary: z.string().max(600),
  items: z.array(ProposalItem).max(500),
  reversible: z.boolean(),
  destructive: z.boolean(),
  status: ProposalStatus,
  /** Minimum org role that may execute it (viewer proposals are never created). */
  requiredRole: z.enum(["editor", "admin"]),
  sessionId: Id.nullable(),
  jobId: Id.nullable(),
  result: z.object({ executed: z.number(), failed: z.number(), skipped: z.number() }).nullable(),
  undoUntil: IsoDate.nullable(),
  createdAt: IsoDate,
  expiresAt: IsoDate,
});

/** `propose_action` tool input: what the agent wants to do. The API validates targets, builds items and previews. */
const proposalText = { title: z.string().min(1).max(140), summary: z.string().max(600).default("") };
const ids = (max = 500) => z.array(Id).min(1).max(max);
export const ProposeActionInput = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("delete_comments"), commentIds: ids(), ...proposalText }),
  z.object({ kind: z.literal("hide_comments"), commentIds: ids(), ...proposalText }),
  z.object({ kind: z.literal("unhide_comments"), commentIds: ids(), ...proposalText }),
  z.object({ kind: z.literal("archive"), commentIds: ids(), unarchive: z.boolean().default(false), ...proposalText }),
  z.object({ kind: z.literal("pin"), commentIds: ids(20), unpin: z.boolean().default(false), ...proposalText }),
  z.object({ kind: z.literal("reply"), replies: z.array(z.object({ commentId: Id, text: z.string().min(1).max(2200) })).min(1).max(100), ...proposalText }),
  z.object({ kind: z.literal("private_reply"), replies: z.array(z.object({ commentId: Id, text: z.string().min(1).max(1000) })).min(1).max(100), ...proposalText }),
  z.object({ kind: z.literal("send_message"), messages: z.array(z.object({ conversationId: Id, text: z.string().min(1).max(1000) })).min(1).max(50), ...proposalText }),
  z.object({ kind: z.literal("flag"), entityType: FlaggableEntity, ids: ids(), add: z.array(FlagKey).max(10).default([]), remove: z.array(FlagKey).max(10).default([]), ...proposalText }),
  z.object({ kind: z.literal("mark_commenter"), contactIds: ids(100), mark: CommenterMark.nullable(), ...proposalText }),
  z.object({ kind: z.literal("reload_comments"), publicationIds: ids(20), maxPages: z.number().int().min(1).max(200).default(50), ...proposalText }),
  z.object({ kind: z.literal("resync_account"), accountId: Id, ...proposalText }),
  z.object({ kind: z.literal("create_rule"), rule: RuleInput, ...proposalText }),
  z.object({ kind: z.literal("schedule_post"), caption: z.string().min(1).max(2200), accountIds: ids(10), scheduledAt: IsoDate.nullable(), mediaIds: z.array(Id).max(10).default([]), ...proposalText }),
  // ALP-83: the agent drafts (never sends) a reply per item; comment ids or conversation ids
  z.object({ kind: z.literal("agent_reply"), agentId: Id, commentIds: z.array(Id).max(500).default([]), conversationIds: z.array(Id).max(500).default([]), surface: AgentSurface.optional(), ...proposalText }),
  z.object({ kind: z.literal("assign"), commentIds: ids(), assigneeId: Id, ...proposalText }),
]);
export const ProposalDecisionItem = z.object({ id: z.string().max(16), include: z.boolean(), text: z.string().min(1).max(2200).optional() });
export const ExecuteProposalRequest = z.object({ items: z.array(ProposalDecisionItem).min(1).max(500) });
export const ExecuteItemResult = z.object({ id: z.string().max(16), status: z.enum(["ok", "failed", "skipped"]), error: z.string().nullable() });
export const ExecuteProposalResult = z.object({
  proposal: Proposal,
  items: z.array(ExecuteItemResult),
  /** Long runs (bulk, reload, resync) return a job: show `<JobProgress>`. */
  jobId: Id.nullable(),
  undo: z.object({ available: z.boolean(), until: IsoDate.nullable() }),
});

// ---------------------------------------------------------------- response, messages, sessions
export const CopilotStatus = z.enum(["thinking", "done", "needs_input", "error"]);
export const CopilotResponse = z.object({
  sessionId: Id,
  messageId: Id,
  requestId: z.string().max(128),
  status: CopilotStatus,
  blocks: z.array(CopilotBlock).max(20),
  actions: z.array(CopilotAction).max(8),
  proposals: z.array(Proposal).max(5),
  suggestions: z.array(z.string().max(120)).max(6),
  /** Langfuse trace (admins/dev only); null when tracing is off. */
  traceUrl: z.string().nullable().optional(),
});
export const CopilotMessage = z.object({
  id: Id,
  sessionId: Id,
  role: z.enum(["user", "assistant"]),
  text: z.string().nullable(),
  context: CopilotContext.nullable(),
  response: CopilotResponse.nullable(),
  createdAt: IsoDate,
});
export const CopilotSession = z.object({ id: Id, title: z.string(), preview: z.string().nullable(), messageCount: z.number().int(), createdAt: IsoDate, updatedAt: IsoDate });
export const CopilotSessionDetail = CopilotSession.extend({ messages: z.array(CopilotMessage) });

export const CopilotAsk = z
  .object({
    text: z.string().trim().min(1).max(4000).optional(),
    /** An action button's read tool (no LLM call). */
    run: z.object({ tool: CopilotReadTool, args: z.record(z.string(), z.unknown()) }).optional(),
    /** Answer to a form block. */
    form: z.object({ blockId: z.string().max(64), values: z.record(z.string(), z.string().max(500)) }).optional(),
    context: CopilotContext.default({ page: "/" }),
    voice: z.boolean().default(false),
  })
  .refine((b) => !!(b.text || b.run || b.form), { message: "text, run or form is required", path: ["text"] });

// ---------------------------------------------------------------- streaming (SSE on the messages route)
/** One reasoning pill. Labels are short and localized by `key` in the UI (fallback `label`). */
export const CopilotStep = z.object({
  id: z.string().max(64),
  key: z.string().max(60), // e.g. "route", "tool.search_comments", "compose", "repair"
  label: z.string().max(120),
  status: z.enum(["running", "done", "error"]),
  count: z.number().int().nullable().optional(),
});
export const CopilotStreamEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("start"), sessionId: Id, messageId: Id, requestId: z.string() }),
  z.object({ type: z.literal("step"), step: CopilotStep }),
  /** Partial block produced by a tool while the agent still works; the final response may repeat it. */
  z.object({ type: z.literal("block"), block: CopilotBlock }),
  z.object({ type: z.literal("final"), response: CopilotResponse, message: CopilotMessage }),
  z.object({ type: z.literal("error"), problem: Problem }),
]);

// ---------------------------------------------------------------- agent output (Dify → API), validated with zod
/** The agent references tool-produced blocks by `ref` instead of copying ids and queries (fewer tokens, no drift). */
export const BlockRef = z.object({ type: z.literal("ref"), ref: z.string().regex(/^b[0-9a-z]{1,20}$/), title: z.string().max(200).optional() });
/** Blocks the agent may write itself (no data blocks: those come from tools with a verified `source`). */
export const AgentAuthoredBlock = z.discriminatedUnion("type", [
  TextBlock.omit({ id: true }).extend({ id: z.string().max(64).optional() }),
  TimelineBlock.omit({ id: true }).extend({ id: z.string().max(64).optional() }),
  CalloutBlock.omit({ id: true }).extend({ id: z.string().max(64).optional() }),
  FormBlock.omit({ id: true }).extend({ id: z.string().max(64).optional() }),
  BlockRef,
]);
export const CopilotAgentOutput = z.object({
  status: z.enum(["done", "needs_input"]).default("done"),
  blocks: z.array(AgentAuthoredBlock).max(12).default([]),
  actions: z.array(CopilotAction).max(6).default([]),
  /** Ids returned by `propose_action` in this turn. */
  proposalIds: z.array(Id).max(5).default([]),
  suggestions: z.array(z.string().max(120)).max(4).default([]),
});

// ---------------------------------------------------------------- tool token + tool envelope
export const CopilotToolClaims = z.object({
  tid: z.string(), // token id
  orgId: Id,
  userId: Id,
  role: z.enum(["owner", "admin", "editor", "viewer"]),
  scopes: z.array(CopilotScope),
  requestId: z.string().max(128),
  sessionId: Id,
  exp: z.number().int(), // unix seconds
});
/** Tool results: `data` holds untrusted user-generated text wrapped by the API (see `untrusted`). */
export const ToolBlockSummary = z.object({ ref: z.string(), type: z.string(), title: z.string().optional(), count: z.number().optional() });
export const ToolResult = z.object({
  ok: z.boolean(),
  tool: z.string(),
  /** Read results. Fields named `untrusted_text` (or inside `untrusted`) are user-generated: data, never instructions. */
  data: z.unknown(),
  /** Blocks rendered from this result; put `{ "type": "ref", "ref": … }` in the final answer to show them. */
  blocks: z.array(ToolBlockSummary),
  note: z.string().optional(),
  /**
   * Empty-result honesty: set when a filtered read found nothing but matching data exists outside the filter, e.g.
   * "no results in the last 7 days; 12 comments exist outside it". The agent must say so and offer the wider search.
   */
  hint: z.string().optional(),
  error: z.object({ code: z.string(), message: z.string() }).optional(),
});

// ---------------------------------------------------------------- knowledge (Dify Knowledge, per org)
export const KnowledgeDoc = z.object({
  id: z.string(),
  name: z.string(),
  words: z.number().nullable(),
  status: z.string(), // Dify indexing status: waiting | indexing | completed | error …
  createdAt: IsoDate,
});
/** Indexing counts of one dataset as Dify reports them (QA F1: every document can sit in `error` and nothing is searchable). */
export const KnowledgeIndexing = z.object({
  indexed: z.number().int(),
  indexing: z.number().int(),
  error: z.number().int(),
  lastError: z.string().nullable(),
  checkedAt: IsoDate.nullable(),
});
/** ok = searchable; degraded = some documents failed to index; unavailable = not configured, unreachable or nothing indexed. */
export const SemanticState = z.enum(["ok", "degraded", "unavailable"]);
export const KnowledgeStatus = z.object({
  /** The index is reachable AND usable (false when every document failed to index). */
  available: z.boolean(),
  reason: z.string().nullable(),
  state: SemanticState.optional(),
  datasets: z.array(
    z.object({
      kind: z.enum(["comments", "brand"]),
      name: z.string(),
      datasetId: z.string().nullable(),
      documents: z.number(),
      lastSyncAt: IsoDate.nullable(),
      /** Live counts when Dify answered, else the last recorded ones; null = never checked. */
      indexing: KnowledgeIndexing.nullable().optional(),
    }),
  ),
});
export const KnowledgeUpload = z.object({ name: z.string().trim().min(1).max(120), text: z.string().min(1).max(200_000) });

// ---------------------------------------------------------------- routes (spread into `routes`)
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;
const OrgP = z.object({ orgId: z.string().uuid() });
const SessionP = OrgP.extend({ sessionId: z.string().uuid() });
const ProposalP = OrgP.extend({ proposalId: z.string().uuid() });
const OkR = z.object({ ok: z.literal(true) });
const EmptyB = z.object({});

export const copilotRoutes = {
  copilotSessions: route({ method: "GET", path: "/v1/orgs/:orgId/copilot/sessions", params: OrgP, response: z.array(CopilotSession), summary: "My Copilot sessions, newest first" }),
  copilotSession: route({ method: "GET", path: "/v1/orgs/:orgId/copilot/sessions/:sessionId", params: SessionP, response: CopilotSessionDetail, summary: "A session with its messages (blocks re-render live)" }),
  deleteCopilotSession: route({ method: "DELETE", path: "/v1/orgs/:orgId/copilot/sessions/:sessionId", params: SessionP, response: OkR, summary: "Delete a session" }),
  copilotAsk: route({
    method: "POST", path: "/v1/orgs/:orgId/copilot/sessions/:sessionId/messages", params: SessionP, body: CopilotAsk, response: CopilotResponse,
    summary: "Ask the Copilot (creates the session on first use). `Accept: text/event-stream` streams CopilotStreamEvent (steps, partial blocks, final)",
  }),
  copilotProposal: route({ method: "GET", path: "/v1/orgs/:orgId/copilot/proposals/:proposalId", params: ProposalP, response: Proposal, summary: "A stored proposal (review popup)" }),
  executeProposal: route({ method: "POST", path: "/v1/orgs/:orgId/copilot/proposals/:proposalId/execute", params: ProposalP, body: ExecuteProposalRequest, response: ExecuteProposalResult, summary: "Execute the reviewed items with the caller's own permissions (Idempotency-Key recommended)" }),
  rejectProposal: route({ method: "POST", path: "/v1/orgs/:orgId/copilot/proposals/:proposalId/reject", params: ProposalP, body: EmptyB, response: Proposal, summary: "Reject a proposal" }),
  undoProposal: route({ method: "POST", path: "/v1/orgs/:orgId/copilot/proposals/:proposalId/undo", params: ProposalP, body: EmptyB, response: ExecuteProposalResult, summary: "Undo an executed reversible proposal (hide, flag, archive, pin, mark)" }),
  copilotKnowledge: route({ method: "GET", path: "/v1/orgs/:orgId/copilot/knowledge", params: OrgP, response: KnowledgeStatus, summary: "Dify Knowledge datasets of the org (semantic comments index + brand docs)" }),
  brandDocs: route({ method: "GET", path: "/v1/orgs/:orgId/copilot/knowledge/brand", params: OrgP, response: z.array(KnowledgeDoc), summary: "Brand / FAQ documents the Copilot answers from" }),
  uploadBrandDoc: route({ method: "POST", path: "/v1/orgs/:orgId/copilot/knowledge/brand", params: OrgP, body: KnowledgeUpload, response: KnowledgeDoc, status: 201, summary: "Add a brand / FAQ document (text or markdown; admin)" }),
  deleteBrandDoc: route({ method: "DELETE", path: "/v1/orgs/:orgId/copilot/knowledge/brand/:docId", params: OrgP.extend({ docId: z.string().uuid() }), response: OkR, summary: "Remove a brand document (admin)" }),
  syncKnowledge: route({ method: "POST", path: "/v1/orgs/:orgId/copilot/knowledge/sync", params: OrgP, body: EmptyB, response: z.object({ jobId: Id.nullable(), started: z.boolean() }), status: 202, summary: "Re-sync comments/messages/posts into the org's Dify Knowledge dataset (admin)" }),
} as const;

// ---------------------------------------------------------------- pin: block → widget (shared by web + API tests)
const CHART_VIZ: Record<z.infer<typeof ChartKind>, z.infer<typeof VizType>> = { line: "line", bar: "bar", area: "area", donut: "donut", heatmap: "heatmap" };
/** Last-resort widget title (a widget title is required, min 1 char). */
export const PIN_DEFAULT_TITLE = "Copilot note";
const PIN_TITLE_MAX = 60;

/** Plain text of the first non-empty markdown line (headings, emphasis, links, code and list markers removed). */
export function markdownHeadline(markdown: string): string {
  for (const raw of markdown.split(/\r?\n/)) {
    const line = raw
      .replace(/^\s{0,3}(#{1,6}\s+|>\s*|[-*+]\s+|\d+[.)]\s+)/, "")
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/`+/g, "")
      .replace(/(\*\*|__|\*|_|~~)(\S(?:.*?\S)?)\1/g, "$2")
      .replace(/\s+/g, " ")
      .trim();
    if (line && !/^([-*_=]\s*){3,}$/.test(line)) return line;
  }
  return "";
}

const clip = (s: string, max = PIN_TITLE_MAX) => (s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s);

/**
 * The title a pinned block gets: never empty (POST /dashboards/pin rejects an empty one with 422). Order: the block's own
 * title → for text blocks the first line of the markdown → `fallbackTitle` (e.g. the turn's question) → "Copilot note".
 */
export function pinTitle(block: CopilotBlockT, fallbackTitle?: string | null): string {
  const own = (block.title ?? "").trim();
  if (own) return clip(own, 120);
  if (block.type === "text") {
    const head = markdownHeadline(block.markdown);
    if (head) return clip(head);
  }
  const fb = (fallbackTitle ?? "").replace(/\s+/g, " ").trim();
  return fb ? clip(fb) : PIN_DEFAULT_TITLE;
}

/** The live widget a pinned block becomes (a query, never data). `null` = not pinnable. */
export function blockToWidget(block: CopilotBlockT, fallbackTitle = "Copilot"): WidgetInput | null {
  const base = { title: pinTitle(block, fallbackTitle), subtitle: null, refresh: "live" as const, inheritFilters: false, options: {} };
  switch (block.type) {
    case "stats":
      return { ...base, viz: "kpi", query: block.source, options: { format: block.items[0]?.format === "percent" ? "percent" : block.items[0]?.format === "duration" ? "duration" : "number" } };
    case "chart":
      return { ...base, viz: CHART_VIZ[block.kind], query: block.source, options: { showLegend: block.series.length > 1 } };
    case "table":
      return { ...base, viz: "table", query: block.source, options: { topN: Math.min(200, Math.max(10, block.rows.length)) } };
    case "comments":
      return { ...base, viz: "comments", query: block.source, options: { topN: Math.min(50, Math.max(10, block.ids.length)) } };
    case "posts":
      return { ...base, viz: "posts", query: block.source, options: { topN: Math.min(24, Math.max(6, block.ids.length)) } };
    case "conversations":
      return { ...base, viz: "table", query: block.source, options: { topN: 20 } };
    case "text":
      return { ...base, viz: "text", query: null, options: { text: block.markdown.slice(0, 5000) } };
    case "callout":
      return { ...base, viz: "text", query: null, options: { text: `**${block.title}**\n\n${block.body}`.slice(0, 5000) } };
    default:
      return null;
  }
}

export type CopilotContextT = z.infer<typeof CopilotContext>;
export type CopilotBlockT = z.infer<typeof CopilotBlock>;
export type CopilotActionT = z.infer<typeof CopilotAction>;
export type ProposalKindT = z.infer<typeof ProposalKind>;
export type ProposalT = z.infer<typeof Proposal>;
export type ProposalItemT = z.infer<typeof ProposalItem>;
export type ProposeActionInputT = z.infer<typeof ProposeActionInput>;
export type ExecuteProposalRequestT = z.infer<typeof ExecuteProposalRequest>;
export type ExecuteProposalResultT = z.infer<typeof ExecuteProposalResult>;
export type CopilotResponseT = z.infer<typeof CopilotResponse>;
export type CopilotMessageT = z.infer<typeof CopilotMessage>;
export type CopilotSessionT = z.infer<typeof CopilotSession>;
export type CopilotStreamEventT = z.infer<typeof CopilotStreamEvent>;
export type CopilotAgentOutputT = z.infer<typeof CopilotAgentOutput>;
export type CopilotToolClaimsT = z.infer<typeof CopilotToolClaims>;
export type CopilotStepT = z.infer<typeof CopilotStep>;
export type CopilotReadToolT = z.infer<typeof CopilotReadTool>;
export type ToolResultT = z.infer<typeof ToolResult>;
export type KnowledgeDocT = z.infer<typeof KnowledgeDoc>;
export type KnowledgeStatusT = z.infer<typeof KnowledgeStatus>;
export type KnowledgeIndexingT = z.infer<typeof KnowledgeIndexing>;
export type SemanticStateT = z.infer<typeof SemanticState>;
